import React, { useEffect, useState } from 'react';
import { employeeWalletAPI, authAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, User, Building2, CreditCard, FileText, Landmark, Shield, CheckCircle, AlertCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export default function EmployeeProfile() {
  const [profile, setProfile] = useState(null);
  const [comp, setComp] = useState(null);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bvnInput, setBvnInput] = useState('');
  const [bvnSubmitting, setBvnSubmitting] = useState(false);
  const [showBvnForm, setShowBvnForm] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    setPwSaving(true);
    try {
      await authAPI.changePassword(currentPw, newPw);
      toast.success('Password changed successfully');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setPwSaving(false); }
  };

  const loadProfile = async () => {
    try {
      const [p, c, s] = await Promise.all([
        employeeWalletAPI.getProfile(),
        employeeWalletAPI.getCompensation(),
        employeeWalletAPI.getSavedRecipients(),
      ]);
      setProfile(p); setComp(c); setSaved(s);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadProfile(); }, []);

  const handleBvnSubmit = async (e) => {
    e.preventDefault();
    if (!bvnInput || bvnInput.length !== 11 || !/^\d{11}$/.test(bvnInput)) {
      toast.error('BVN must be exactly 11 digits');
      return;
    }
    setBvnSubmitting(true);
    try {
      const res = await employeeWalletAPI.updateBVN(bvnInput);
      if (res.account_created) {
        toast.success(`BVN saved & wallet account created: ${res.account_number}`);
      } else if (res.account_error) {
        toast.success('BVN saved! ' + res.account_error);
      } else {
        toast.success(res.message || 'BVN updated successfully!');
      }
      setBvnInput('');
      setShowBvnForm(false);
      await loadProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update BVN');
    } finally { setBvnSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;
  if (!profile) return null;

  const Section = ({ title, icon: Icon, children }) => (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</h2>
      </div>
      <Card className="border-gray-200"><CardContent className="p-4 space-y-2">{children}</CardContent></Card>
    </div>
  );

  const Row = ({ label, value }) => (
    <div className="flex justify-between py-0.5">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-xs font-medium text-gray-900">{value || '\u2014'}</span>
    </div>
  );

  return (
    <div className="space-y-5 pb-6" data-testid="employee-profile">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center text-lg font-bold">
          {profile.first_name?.[0]}{profile.last_name?.[0]}
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">{profile.first_name} {profile.last_name}</h1>
          <p className="text-sm text-gray-500">{profile.job_title}</p>
          <p className="text-xs text-gray-400">{profile.email}</p>
        </div>
      </div>

      {/* BVN & Wallet Activation Banner */}
      {!profile.has_bvn ? (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="bvn-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-900">Activate your wallet</h3>
                <p className="text-xs text-amber-700 mt-0.5">Add your BVN (Bank Verification Number) to activate your Pebbles wallet and receive payments directly.</p>
                {!showBvnForm ? (
                  <Button size="sm" onClick={() => setShowBvnForm(true)} data-testid="add-bvn-btn"
                    className="mt-3 h-8 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg">
                    Add BVN
                  </Button>
                ) : (
                  <form onSubmit={handleBvnSubmit} className="mt-3 flex items-center gap-2">
                    <input
                      type="text" inputMode="numeric" maxLength={11} value={bvnInput}
                      onChange={e => setBvnInput(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      placeholder="Enter 11-digit BVN"
                      className="h-8 px-3 rounded-lg border border-amber-300 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 w-44"
                      data-testid="bvn-input"
                    />
                    <Button type="submit" size="sm" disabled={bvnSubmitting || bvnInput.length !== 11} data-testid="submit-bvn-btn"
                      className="h-8 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg">
                      {bvnSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setShowBvnForm(false); setBvnInput(''); }}
                      className="h-8 text-xs text-amber-700">Cancel</Button>
                  </form>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-emerald-200 bg-emerald-50/50" data-testid="bvn-verified-banner">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-emerald-900">BVN verified</h3>
                <p className="text-xs text-emerald-700">
                  {profile.wallet_active && profile.account_number
                    ? `Wallet is active — Account: ${profile.account_number}`
                    : profile.account_number
                    ? `Account ${profile.account_number} created. Wallet activation pending.`
                    : 'Your BVN is on file. Wallet account creation pending — your HR admin will complete this.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Section title="Personal Information" icon={User}>
        <Row label="Full Name" value={`${profile.first_name} ${profile.middle_name || ''} ${profile.last_name}`.trim()} />
        <Row label="Email" value={profile.email} />
        <Row label="Phone" value={profile.phone} />
        <Row label="Gender" value={profile.gender} />
        <Row label="Date of Birth" value={profile.date_of_birth ? new Date(profile.date_of_birth).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null} />
        <Row label="Employee ID" value={profile.employee_id} />
        <Row label="Department" value={profile.department} />
        <Row label="Start Date" value={profile.hire_date ? new Date(profile.hire_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null} />
      </Section>

      <Section title="Wallet & Banking" icon={Shield}>
        <Row label="BVN" value={profile.has_bvn ? 'Verified' : 'Not provided'} />
        <Row label="Bank" value={profile.bank_name} />
        <Row label="Account No" value={profile.account_number} />
        <Row label="Account Name" value={profile.account_name} />
        <Row label="Wallet Status" value={profile.wallet_active ? 'Active' : 'Pending'} />
      </Section>

      <Section title="Compensation" icon={CreditCard}>
        <Row label="Gross Salary" value={profile.gross_salary ? `NGN ${Number(profile.gross_salary).toLocaleString()}` : null} />
        <Row label="Employment Type" value={profile.employment_type?.replace('_', ' ')} />
      </Section>

      {saved.length > 0 && (
        <Section title="Saved Recipients" icon={Landmark}>
          {saved.map(r => (
            <div key={r.id} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[9px] font-bold text-gray-500">
                  {r.name.split(' ').map(n => n[0]).join('')}
                </div>
                <span className="text-xs text-gray-900">{r.name}</span>
              </div>
              <span className="text-[10px] text-gray-400">{r.bank_name} ****{r.account_number.slice(-4)}</span>
            </div>
          ))}
        </Section>
      )}

      <Section title="Documents" icon={FileText}>
        {['Employment Letter', 'Latest Payslip (Feb 2026)', 'Tax Deduction Card (2026)'].map((doc, i) => (
          <div key={i} className="flex items-center justify-between py-1">
            <span className="text-xs text-gray-700">{doc}</span>
            <Button size="sm" variant="ghost" className="text-xs text-pink-500 h-6 px-2">Download</Button>
          </div>
        ))}
      </Section>

      <Section title="Change Password" icon={Lock}>
        <form onSubmit={handleChangePassword} className="space-y-3">
          <div>
            <Label className="text-xs text-gray-500">Current Password</Label>
            <div className="relative mt-1">
              <Input type={showPw ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)}
                placeholder="Enter current password" className="h-9 text-sm pr-9" data-testid="current-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-gray-500">New Password</Label>
            <Input type={showPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)}
              placeholder="At least 8 characters" className="h-9 text-sm mt-1" data-testid="new-password-input" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Confirm New Password</Label>
            <Input type={showPw ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
              placeholder="Re-enter new password" className="h-9 text-sm mt-1" data-testid="confirm-password-input" />
            {confirmPw && newPw !== confirmPw && <p className="text-xs text-red-500 mt-1">Passwords do not match</p>}
          </div>
          <Button type="submit" disabled={pwSaving || !currentPw || !newPw || !confirmPw} variant="outline"
            className="border-gray-200 text-xs h-8" data-testid="change-password-btn">
            {pwSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
            Update Password
          </Button>
        </form>
      </Section>
    </div>
  );
}
